import { defineStore } from 'pinia';
import moment from 'moment';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type {
    PaymentStatisticsStateType,
    PaymentStatisticsGettersType,
    PaymentStatisticsActionsType,
} from '@/types/stores/PaymentStatisticsStoreTypes';
import type { PaymentConfigurationType } from '@/types/PaymentConfigurationType';
import type { PaymentStatisticsFilterType, PaymentStatisticsDataType } from '@/types/statistics/PaymentStatisticsType';
import type { FilterChipType } from '@/types/statistics/FilterChipType';
import { formatDateRangeChip } from '@/utils/storeUtils';
import { getIntelligentChartColors } from '@/utils/chartColorUtils';
import i18n from '@/plugins/i18n';
import { VDataTableServer } from 'vuetify/components';
import type { LineChartDataType } from '@/types/ChartType';
import type { PaymentMetricType } from '@/types/statistics/PaymentMetricType';
import type { CustomResponseType } from '@/types/CustomAxiosReponseType';

const createDefaultFilters = (): PaymentStatisticsFilterType => ({
    dateRange: {
        start: moment().subtract(7, 'days').startOf('day').toDate(),
        end: moment().endOf('day').toDate(),
    },
    groupedBy: 'day',
    paymentConfigurationId: undefined,
});

const createDefaultData = (): PaymentStatisticsDataType => ({
    groupedBy: 'day',
    startDate: '',
    endDate: '',
    summary: {
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        successRate: 0,
        totalAmount: 0,
    },
    groups: [],
});

const createDefaultHeaders = (): VDataTableServer['$props']['headers'] => [
    {
        title: 'statistics.payments.table.period',
        key: 'type',
        sortable: true,
    },
    {
        title: 'statistics.payments.table.totalTransactions',
        key: 'total_transactions',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.payments.table.successfulTransactions',
        key: 'successful_transactions',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.payments.table.failedTransactions',
        key: 'failed_transactions',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.payments.table.successRate',
        key: 'success_rate',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.payments.table.totalAmount',
        key: 'total_amount',
        sortable: true,
        align: 'end',
    },
];

export const usePaymentStatisticsStore = defineStore<
    'paymentStatistics',
    PaymentStatisticsStateType,
    PaymentStatisticsGettersType,
    PaymentStatisticsActionsType
>('paymentStatistics', {
    state: (): PaymentStatisticsStateType => ({
        isLoading: false,
        isPaymentsLoading: false,
        filters: createDefaultFilters(),
        data: createDefaultData(),
        headers: createDefaultHeaders(),
        paymentConfigurations: [],
    }),

    getters: {
        getFilteredData: state => state.data || createDefaultData(),
        getTotalTransactions: state => state.data?.summary?.totalTransactions || 0,
        hasData: state => (state.data?.groups?.length || 0) > 0,
        getFilterChips: (state): FilterChipType[] => {
            const chips: FilterChipType[] = [];
            const { t } = i18n.global;

            chips.push({
                key: 'groupedBy',
                label: `${t('common.groupedBy')}: ${t(`common.grouping.${state.filters.groupedBy}`)}`,
                value: state.filters.groupedBy,
                icon: 'mdi-group',
                removable: false,
            });

            if (state.filters.dateRange?.start && state.filters.dateRange?.end) {
                chips.push(formatDateRangeChip(state.filters.dateRange));
            }

            if (state.filters.paymentConfigurationId && state.filters.paymentConfigurationId.length > 0) {
                const configNames = state.paymentConfigurations
                    .filter(config => state.filters.paymentConfigurationId?.includes(config.id))
                    .map(config => config.name)
                    .join(', ');
                chips.push({
                    key: 'paymentConfigurationId',
                    label: `${t('statistics.filters.paymentConfiguration')}: ${configNames}`,
                    value: state.filters.paymentConfigurationId,
                    icon: 'mdi-credit-card-settings',
                    removable: true,
                });
            }

            return chips;
        },
        getTranslatedHeaders: state => {
            const { t } = i18n.global;

            return (
                state.headers?.map(header => ({
                    ...header,
                    title: header.title ? t(header.title) : header.title,
                })) || []
            );
        },
        getPaymentConfigurationOptions: (state: PaymentStatisticsStateType) => {
            if (!state.paymentConfigurations.length) {
                return [];
            }

            return state.paymentConfigurations.map(config => ({
                title: config.name,
                value: config.id,
            }));
        },

        getChartData:
            state =>
                (metric: PaymentMetricType): LineChartDataType => {
                    if (!state.data?.groups?.length) {
                        return { labels: [], datasets: [] };
                    }

                    const labels = state.data.groups.map(group => group.label);

                    const configMap = new Map<number, { name: string; data: number[] }>();

                    for (const group of state.data.groups) {
                        for (const config of group.data) {
                            if (!configMap.has(config.payment_configuration_id)) {
                                configMap.set(config.payment_configuration_id, {
                                    name: config.payment_configuration_name,
                                    data: [],
                                });
                            }
                            const configData = configMap.get(config.payment_configuration_id)!;
                            configData.data.push(config[metric]);
                        }
                    }

                    const configEntries = Array.from(configMap.entries());
                    const colorPairs = getIntelligentChartColors(configEntries.length);

                    const datasets = configEntries.map(([_id, configInfo], index) => {
                        const colorPair = colorPairs[index];

                        return {
                            label: configInfo.name,
                            data: configInfo.data,
                            borderColor: colorPair.borderColor,
                            backgroundColor: colorPair.backgroundColor,
                            fill: false,
                            tension: 0.1,
                        };
                    });

                    return { labels, datasets };
                },
    },

    actions: {
        async fetchPaymentConfigurations() {
            this.isPaymentsLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: '/payment-configurations',
                onSuccess: (response: CustomResponseType<PaymentConfigurationType[]>) => {
                    this.paymentConfigurations = response.data.items;
                },
            });
            this.isPaymentsLoading = false;
        },

        async fetchData() {
            this.isLoading = true;

            await new Promise(resolve => setTimeout(resolve, 500));

            this.data = {
                groupedBy: this.filters.groupedBy,
                startDate: this.filters.dateRange?.start?.toISOString() || '',
                endDate: this.filters.dateRange?.end?.toISOString() || '',
                summary: {
                    totalTransactions: 1580,
                    successfulTransactions: 1512,
                    failedTransactions: 68,
                    successRate: 95.7,
                    totalAmount: 248750.5,
                },
                groups: [
                    {
                        type: 'day',
                        label: '15.12.2025',
                        data: [
                            {
                                payment_configuration_id: 1,
                                payment_configuration_name: 'ČSOB Terminál - Hlavní pobočka',
                                adapter_type: 'csob_terminal',
                                total_transactions: 58,
                                successful_transactions: 55,
                                failed_transactions: 3,
                                success_rate: 94.83,
                                total_amount: 34250.0,
                            },
                            {
                                payment_configuration_id: 2,
                                payment_configuration_name: 'Stripe Terminal - Online platby',
                                adapter_type: 'stripe_terminal',
                                total_transactions: 42,
                                successful_transactions: 41,
                                failed_transactions: 1,
                                success_rate: 97.62,
                                total_amount: 18900.5,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '16.12.2025',
                        data: [
                            {
                                payment_configuration_id: 1,
                                payment_configuration_name: 'ČSOB Terminál - Hlavní pobočka',
                                adapter_type: 'csob_terminal',
                                total_transactions: 62,
                                successful_transactions: 59,
                                failed_transactions: 3,
                                success_rate: 95.16,
                                total_amount: 37850.0,
                            },
                            {
                                payment_configuration_id: 2,
                                payment_configuration_name: 'Stripe Terminal - Online platby',
                                adapter_type: 'stripe_terminal',
                                total_transactions: 45,
                                successful_transactions: 44,
                                failed_transactions: 1,
                                success_rate: 97.78,
                                total_amount: 20150.0,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '17.12.2025',
                        data: [
                            {
                                payment_configuration_id: 1,
                                payment_configuration_name: 'ČSOB Terminál - Hlavní pobočka',
                                adapter_type: 'csob_terminal',
                                total_transactions: 55,
                                successful_transactions: 52,
                                failed_transactions: 3,
                                success_rate: 94.55,
                                total_amount: 32100.0,
                            },
                            {
                                payment_configuration_id: 2,
                                payment_configuration_name: 'Stripe Terminal - Online platby',
                                adapter_type: 'stripe_terminal',
                                total_transactions: 48,
                                successful_transactions: 46,
                                failed_transactions: 2,
                                success_rate: 95.83,
                                total_amount: 21800.0,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '18.12.2025',
                        data: [
                            {
                                payment_configuration_id: 1,
                                payment_configuration_name: 'ČSOB Terminál - Hlavní pobočka',
                                adapter_type: 'csob_terminal',
                                total_transactions: 60,
                                successful_transactions: 57,
                                failed_transactions: 3,
                                success_rate: 95.0,
                                total_amount: 35500.0,
                            },
                            {
                                payment_configuration_id: 2,
                                payment_configuration_name: 'Stripe Terminal - Online platby',
                                adapter_type: 'stripe_terminal',
                                total_transactions: 50,
                                successful_transactions: 48,
                                failed_transactions: 2,
                                success_rate: 96.0,
                                total_amount: 22900.0,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '19.12.2025',
                        data: [
                            {
                                payment_configuration_id: 1,
                                payment_configuration_name: 'ČSOB Terminál - Hlavní pobočka',
                                adapter_type: 'csob_terminal',
                                total_transactions: 65,
                                successful_transactions: 62,
                                failed_transactions: 3,
                                success_rate: 95.38,
                                total_amount: 38750.0,
                            },
                            {
                                payment_configuration_id: 2,
                                payment_configuration_name: 'Stripe Terminal - Online platby',
                                adapter_type: 'stripe_terminal',
                                total_transactions: 52,
                                successful_transactions: 50,
                                failed_transactions: 2,
                                success_rate: 96.15,
                                total_amount: 23800.0,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '20.12.2025',
                        data: [
                            {
                                payment_configuration_id: 1,
                                payment_configuration_name: 'ČSOB Terminál - Hlavní pobočka',
                                adapter_type: 'csob_terminal',
                                total_transactions: 63,
                                successful_transactions: 59,
                                failed_transactions: 4,
                                success_rate: 93.65,
                                total_amount: 36200.0,
                            },
                            {
                                payment_configuration_id: 2,
                                payment_configuration_name: 'Stripe Terminal - Online platby',
                                adapter_type: 'stripe_terminal',
                                total_transactions: 47,
                                successful_transactions: 46,
                                failed_transactions: 1,
                                success_rate: 97.87,
                                total_amount: 21950.0,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '21.12.2025',
                        data: [
                            {
                                payment_configuration_id: 1,
                                payment_configuration_name: 'ČSOB Terminál - Hlavní pobočka',
                                adapter_type: 'csob_terminal',
                                total_transactions: 57,
                                successful_transactions: 54,
                                failed_transactions: 3,
                                success_rate: 94.74,
                                total_amount: 33400.0,
                            },
                            {
                                payment_configuration_id: 2,
                                payment_configuration_name: 'Stripe Terminal - Online platby',
                                adapter_type: 'stripe_terminal',
                                total_transactions: 49,
                                successful_transactions: 47,
                                failed_transactions: 2,
                                success_rate: 95.92,
                                total_amount: 22500.0,
                            },
                        ],
                    },
                ],
            };

            this.isLoading = false;
        },
        resetFilters() {
            this.filters = createDefaultFilters();
        },

        async removeFilter(filterKey: string) {
            const defaultFilters = createDefaultFilters();

            switch (filterKey) {
            case 'dateRange':
                this.filters.dateRange = defaultFilters.dateRange;
                break;
            case 'groupedBy':
                this.filters.groupedBy = defaultFilters.groupedBy;
                break;
            case 'paymentConfigurationId':
                this.filters.paymentConfigurationId = undefined;
                break;
            default:
                console.warn(`Unknown filter key: ${filterKey}`);

                return;
            }

            await this.fetchData();
        },
    },
});
